import { Alert, Button, ConfigProvider, Drawer, Flex, Input, Skeleton, Space, Typography } from 'antd'
import { useEffect, useReducer, useState } from 'react'
import { faEnvelope, faSave, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconAndTextButton from '../IconAndTextButton'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import InfoLinks from './InfoLinks'
import ManageProposalPayments from './ManageProposalPayments'
import ProposalAttachments from './ProposalAttachments'
import Workareas from './Workareas'
import SettingsTitledCard from '../settings/SettingsTitledCard'
import {
  ActionType,
  defaultState,
  initialize,
  preparePayloadForSave,
  calculateTotal,
  canSaveProposal,
  hasShownWorkArea,
  addAttachment,
  isCreatingProposal,
  reducer,
  setAttachments,
  setCoverLetter,
  setInternalName,
  setTermsAndConditions
} from './proposalsModel'
import { Action as LineItemAction } from './lineItemsModel'
import { useCreateProposalFromDrawerMutation, useCreateProposalModel, useProposalDraft, useUpdateProposalDraftMutation } from './proposalQueries'
import { centsToDollarsDisplay } from '../displayFormatters'
import { formatEstimatedHours } from './proposalAttachmentsModel'
import './ManageProposalDrawer.css'

const BRAND_ORANGE = '#FF5C35'

type Props = {
  proposalId?: number
  jobId?: number
  open: boolean
  onClose: () => void
  onSaved: () => void
  onLinkCustomerToQbo?: () => void
  onLinkCostCodeToQbo?: (costCodeId: number) => void
  onPreview?: (proposalId: number) => void
  onSend?: (proposalId: number, proposalTotal: number) => void
}

const ManageProposalDrawer = ({ proposalId, jobId, open, onClose, onSaved, onLinkCustomerToQbo, onLinkCostCodeToQbo, onPreview, onSend }: Props) => {
  const isCreating = isCreatingProposal({ proposalId, jobId })
  const draftQuery = useProposalDraft(proposalId, open && !isCreating)
  const createQuery = useCreateProposalModel(jobId ?? 0, open && isCreating)
  const proposalQuery = isCreating ? createQuery : draftQuery
  const [isInitialized, setIsInitialized] = useState(false)
  const [model, dispatch] = useReducer(reducer, defaultState())

  useEffect(() => {
    if (!open) {
      setIsInitialized(false)
      dispatch({ type: ActionType.DataLoaded, value: defaultState() })
    }
  }, [open])

  useEffect(() => {
    if (open && proposalQuery.data) {
      dispatch({ type: ActionType.DataLoaded, value: initialize(proposalQuery.data) })
      setIsInitialized(true)
    }
  }, [open, isCreating, proposalId, jobId, proposalQuery.data])

  const updateDraft = useUpdateProposalDraftMutation(() => {
    onSaved()
    onClose()
  })

  const createProposal = useCreateProposalFromDrawerMutation(() => {
    onSaved()
    onClose()
  })

  const isSaving = updateDraft.isLoading || createProposal.isLoading

  const dispatchLineItem = (id: number) => (lineItemAction: LineItemAction) =>
    dispatch({ type: ActionType.LineItemChanged, id, lineItemAction })

  const workAreaShown = hasShownWorkArea(model.lineItems)
  const canSave = canSaveProposal({
    proposalId, jobId, isInitialized, isLoading: proposalQuery.isLoading, isSaving,
    hasShownWorkArea: workAreaShown
  })

  const save = () => {
    if (!canSave) {
      return
    }
    const payload = preparePayloadForSave(model)
    if (isCreating) {
      createProposal.mutate(payload)
      return
    }
    updateDraft.mutate({ proposalId: proposalId!, model: payload })
  }

  // The action receives the proposal's id. When creating, that id only exists once the
  // save has come back, so it has to be threaded through from the response rather than
  // read from props — see SYN-3779.
  const saveThen = (action: (savedProposalId?: number) => void) => (shouldSave: boolean) => {
    if (shouldSave && canSave) {
      const payload = preparePayloadForSave(model)
      if (isCreating) {
        createProposal.mutate(payload, { onSuccess: createdProposalId => action(createdProposalId) })
        return
      }
      updateDraft.mutate({ proposalId: proposalId!, model: payload }, { onSuccess: () => action(proposalId) })
      return
    }
    action(proposalId)
  }

  return <Drawer
    open={open}
    onClose={onClose}
    placement="bottom"
    height="95%"
    maskClosable={false}
    styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f5f7fa' } }}
  >
    <div className="manage-proposal__header">
      <Typography.Text strong style={{ fontSize: '16px' }}>{isCreating ? 'Create Proposal' : 'Manage Proposal'}</Typography.Text>
      <Space size="large">
        <Typography.Text>
          <Typography.Text type="secondary">Total Estimated Hours: </Typography.Text>
          <Typography.Text strong>{formatEstimatedHours(model.estimatedHours)}</Typography.Text>
          <Typography.Text type="secondary"> &nbsp;|&nbsp; Final Price: </Typography.Text>
          <Typography.Text strong>{centsToDollarsDisplay(calculateTotal(model.lineItems))}</Typography.Text>
        </Typography.Text>
        <Button
          type="text"
          aria-label="Close"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={onClose}
        />
      </Space>
    </div>

    <div className="manage-proposal__body">
      {proposalQuery.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {proposalQuery.isError && <Alert
        type="error"
        message="Error loading data"
        description={(proposalQuery.error as Error)?.message}
      />}

      {!proposalQuery.isLoading && !proposalQuery.isError &&
        <Flex vertical gap="large" className="manage-proposal__content">
            <SettingsTitledCard
              title="Description"
              description="Name this proposal for internal reference and write the message your customer sees first."
            >
              <label className="manage-proposal__field">
                <div>Internal Proposal Name</div>
                <Input
                  value={model.internalName ?? ''}
                  placeholder="My First Proposal"
                  onChange={e => dispatch(setInternalName(e.target.value))}
                />
              </label>

              <div className="manage-proposal__cover-letter">
                <div>Cover Letter</div>
                <Typography.Text type="secondary">
                  Shown at the top of the proposal, above the work areas, and included in the PDF.
                </Typography.Text>
                <RichTextEditor
                  value={model.coverLetter ?? ''}
                  placeholder="Proposal Description"
                  allowImages
                  onChange={value => dispatch(setCoverLetter(value))}
                />
              </div>

              <div className="manage-proposal__cover-letter">
                <ProposalAttachments
                  attachments={model.attachments ?? []}
                  customerId={model.customerId ?? 0}
                  jobId={model.jobId}
                  onChange={value => dispatch(setAttachments(value))}
                  onAdd={value => dispatch(addAttachment(value))}
                  embedded
                />
              </div>
            </SettingsTitledCard>

            <SettingsTitledCard
              title="Groups and Work Areas"
              description="Manage how Groups, Work Areas, and Items are displayed to the client on your proposal."
            >
              {!workAreaShown && <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Select at least one work area to show before saving this proposal."
              />}
              <ConfigProvider theme={{ components: { Checkbox: { colorPrimary: BRAND_ORANGE, algorithm: true } } }}>
                <Workareas model={model} dispatch={dispatch} dispatchLineItem={dispatchLineItem} redesign />
              </ConfigProvider>
            </SettingsTitledCard>

            <SettingsTitledCard
              title="Terms &amp; Conditions"
              description="The terms shown on the proposal and included in the PDF."
            >
              <RichTextEditor
                value={model.termsAndConditions}
                onChange={value => dispatch(setTermsAndConditions(value))}
              />
            </SettingsTitledCard>

            <SettingsTitledCard
              title="Links"
              description="Add links your customer can open from the proposal."
            >
              <InfoLinks links={model.infoLinks} dispatch={dispatch} redesign />
            </SettingsTitledCard>

            <ManageProposalPayments
              model={model}
              dispatch={dispatch}
              onLinkCustomerToQbo={saveThen(() => onLinkCustomerToQbo?.())}
              onLinkCostCodeToQbo={(costCodeId, shouldSave) => saveThen(() => onLinkCostCodeToQbo?.(costCodeId))(shouldSave)}
            />
        </Flex>}
    </div>

    <div className="manage-proposal__footer">
      <Button onClick={onClose}>Close</Button>
      <Button
        onClick={() => saveThen(savedProposalId => {
          if (typeof savedProposalId === 'number') {
            onPreview?.(savedProposalId)
          }
        })(true)}
        disabled={isCreating || !canSave}
      >Save &amp; Preview</Button>
      <IconAndTextButton
        type="primary"
        icon={faSave}
        text={isCreating ? "Create" : "Save"}
        onClick={save}
        disabled={!canSave}
      />
      <IconAndTextButton
        type="primary"
        icon={faEnvelope}
        text="Send Now"
        onClick={() => saveThen(savedProposalId => {
          // Guard on the type, not just on undefined: if this frontend runs against an
          // api that predates SYN-3779, the save responds with an empty body and we fall
          // back to today's behaviour rather than opening the sheet on a bogus id.
          if (typeof savedProposalId === 'number') {
            onSend?.(savedProposalId, calculateTotal(model.lineItems))
          }
        })(true)}
        disabled={!canSave}
      />
    </div>
  </Drawer>
}

export default ManageProposalDrawer
